import { Link } from "react-router";
import { ArrowRight, MessageSquare, FileText, Package, Zap, Truck, CheckCircle2 } from "lucide-react";
import { GradientSectionHeading, GradientSectionHeadingOnDark } from "../components/GradientSectionHeading";
import { motion } from "motion/react";

export function ProcessPage() {
  return (
    <div className="min-h-screen">
      <section className="relative bg-[#F6F2EB] py-64 overflow-hidden">
        {/* Animated Grid Background - Hidden for now to show blur.png */}
        <div className="absolute inset-0 z-0 pointer-events-none opacity-50 hidden">
          <svg width="100%" height="100%" viewBox="0 0 1920 720" preserveAspectRatio="xMidYMid slice" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ shapeRendering: "geometricPrecision" }}>
            <defs>
              <radialGradient id="paint0_radial" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(960 360.002) rotate(180) scale(711 897.564)">
                <stop offset="0.236637" stopColor="#F7F4ED"></stop>
                <stop offset="0.727046" stopColor="#FF8819"></stop>
                <stop offset="0.817006" stopColor="#FF339E"></stop>
                <stop offset="0.91787" stopColor="#FFA6F9"></stop>
                <stop offset="1" stopColor="#4E93FF"></stop>
              </radialGradient>
            </defs>
            <g id="vertical-lines">
              {[64, 128, 192, 256, 320, 384, 448, 512, 576, 640, 704, 768, 832, 896, 960, 1024, 1088, 1152, 1216, 1280, 1344, 1408, 1472, 1536, 1600, 1664, 1728, 1792, 1856].map((x) => {
                const delay = 0.05 * ((960 - Math.abs(x - 960)) / 64);
                return (
                  <g key={`v-${x}`}>
                    <path d={`M${x} 0V360`} stroke="url(#paint0_radial)" strokeWidth="1.8" style={{ strokeDasharray: 360, strokeDashoffset: 360, animation: `0.5s ease-out ${delay.toFixed(2)}s 1 normal forwards running drawLine` }} />
                    <path d={`M${x} 720V360`} stroke="url(#paint0_radial)" strokeWidth="1.8" style={{ strokeDasharray: 360, strokeDashoffset: 360, animation: `0.5s ease-out ${delay.toFixed(2)}s 1 normal forwards running drawLine` }} />
                  </g>
                );
              })}
            </g>
            <g id="horizontal-lines">
              {[65, 130, 195, 260, 325, 390, 455, 520, 585, 650, 715].map((y) => {
                const delay = 0.05 * ((390 - Math.abs(y - 390)) / 65);
                return (
                  <g key={`h-${y}`}>
                    <path d={`M0 ${y}H960`} stroke="url(#paint0_radial)" strokeWidth="1.8" style={{ strokeDasharray: 960, strokeDashoffset: 960, animation: `0.5s ease-out ${delay.toFixed(2)}s 1 normal forwards running drawLine` }} />
                    <path d={`M1920 ${y}H960`} stroke="url(#paint0_radial)" strokeWidth="1.8" style={{ strokeDasharray: 960, strokeDashoffset: 960, animation: `0.5s ease-out ${delay.toFixed(2)}s 1 normal forwards running drawLine` }} />
                  </g>
                );
              })}
            </g>
          </svg>
        </div>

        {/* Premium Glow Background - Mobile */}
        <div className="2xl:hidden absolute inset-0 flex items-center justify-center overflow-hidden pointer-events-none">
          <div className="pointer-events-none absolute left-1/2 h-full w-full -translate-x-1/2 top-8">
            <motion.div
              initial={{ opacity: 0, y: 120 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 3.5, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="absolute inset-x-0 bottom-0 flex h-full justify-center blur-[60px]"
            >
              <div className="absolute -bottom-[calc(1601px/2)] h-[1401px] w-[1568px] rounded-full bg-[#53A4CF]" />
              <div className="absolute -bottom-[calc(600px/2+100px)] h-[913px] w-[1266px] rounded-[100%] bg-blue-500" />
              <div className="absolute -bottom-[calc(700px/2+100px)] h-[933px] w-[1266px] rounded-[100%] bg-[#FFA6F9]" />
              <div className="absolute -bottom-[calc(700px/2-40px)] h-[692px] w-[1116px] rounded-[100%] bg-[#DA5B9B]" />
              <div className="absolute -bottom-[calc(500px/2)] h-[560px] w-[981px] rounded-[100%] bg-pink-600" />
              <div className="absolute -bottom-[calc(300px/2+100px)] h-[428px] w-[812px] rounded-[100%] bg-[#F3D573]" />
            </motion.div>
          </div>
        </div>

        {/* Premium Glow Background - Desktop */}
        <div className="hidden 2xl:flex absolute inset-0 z-0 items-center justify-center pointer-events-none">
          <div className="pointer-events-none absolute left-1/2 h-full w-full -translate-x-1/2 top-24">
            <div className="absolute inset-x-0 bottom-0 flex justify-center blur-[80px]">
              {/* Largest Outer Blue - Revealed Last */}
              <motion.div
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 2.5, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                style={{ transformOrigin: 'bottom center' }}
                className="absolute -bottom-[40vw] h-[75vw] w-[75vw] rounded-[100%] bg-[#53A4CF]"
              />
              {/* Pink Layer - Revealed Third */}
              <motion.div
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 2.2, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                style={{ transformOrigin: 'bottom center' }}
                className="absolute -bottom-[30vw] h-[55vw] w-[55vw] rounded-[100%] bg-[#DA5B9B]"
              />
              {/* Yellow Layer - Revealed Second */}
              <motion.div
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 2.0, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                style={{ transformOrigin: 'bottom center' }}
                className="absolute -bottom-[20vw] h-[35vw] w-[35vw] rounded-[100%] bg-[#F3D573]"
              />
              {/* Black Core - Revealed First */}
              <motion.div
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 1.8, delay: 0, ease: [0.16, 1, 0.3, 1] }}
                style={{ transformOrigin: 'bottom center' }}
                className="absolute -bottom-[10vw] h-[15vw] w-[20vw] rounded-[100%] bg-[#000000]"
              />
            </div>
          </div>
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-col items-center text-center">
            <div className="relative max-w-4xl">
              <GradientSectionHeadingOnDark
                as="h1"
                className="mb-6 text-[48px] sm:text-[60px] md:text-[70px] lg:text-[78.1px] font-[700] leading-[1.1] [text-wrap:wrap] text-[#FBFAF7]"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Our Process
              </GradientSectionHeadingOnDark>
              <p className="mx-auto h-auto w-full max-w-[720px] px-[7.6px] text-center text-[18.8px] font-semibold text-[#FBFAF7] transition-colors duration-300 mb-10">
                Simple, transparent, and efficient from inquiry to delivery.
                <br className="hidden sm:block" />
                {" "}Experience seamless packaging production.
              </p>
              <div className="flex justify-center mt-10">
                <Link
                  to="/quote"
                  className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-[#131312] text-sm font-medium text-white transition-colors hover:bg-[#1c1c1b] shadow-[0_4px_6px_-4px_rgba(0,0,0,0.22),0_10px_15px_-3px_rgba(0,0,0,0.18)] sm:h-14 sm:w-auto sm:px-8 sm:text-base"
                >
                  Start Your Project
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <div className="space-y-16">
            <div className="flex flex-col md:flex-row gap-8 items-center">
              <div className="flex-shrink-0">
                <div className="w-24 h-24 bg-[#131312] text-white rounded-2xl flex flex-col items-center justify-center shadow-xl">
                  <span className="text-2xl font-semibold">01</span>
                </div>
              </div>
              <div className="hidden md:block flex-shrink-0">
                <ArrowRight className="size-8 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <div className="bg-[#F6F2EB] rounded-2xl p-8 shadow-sm border border-border/40">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="size-12 flex-shrink-0 bg-[#FBFAF7] text-[#131312] rounded-xl flex items-center justify-center border border-border/40 shadow-sm">
                      <MessageSquare className="size-6" />
                    </div>
                    <h2 className="text-2xl font-semibold text-[#131312]">Inquiry & Consultation</h2>
                  </div>
                  <p className="text-lg text-[#131312]/80 mb-6">
                    Reach out via our quote form, email, or phone. Share your packaging requirements, and we'll schedule a consultation to understand your needs in detail.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-center text-[#131312]">
                      <CheckCircle2 className="size-5 mr-3 flex-shrink-0" />
                      <span>Initial consultation and needs assessment</span>
                    </li>
                    <li className="flex items-center text-[#131312]">
                      <CheckCircle2 className="size-5 mr-3 flex-shrink-0" />
                      <span>Material and finish recommendations</span>
                    </li>
                    <li className="flex items-center text-[#131312]">
                      <CheckCircle2 className="size-5 mr-3 flex-shrink-0" />
                      <span>Budget and timeline discussion</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-8 items-center">
              <div className="flex-shrink-0">
                <div className="w-24 h-24 bg-[#131312] text-white rounded-2xl flex flex-col items-center justify-center shadow-xl">
                  <span className="text-2xl font-semibold">02</span>
                </div>
              </div>
              <div className="hidden md:block flex-shrink-0">
                <ArrowRight className="size-8 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <div className="bg-[#F6F2EB] rounded-2xl p-8 shadow-sm border border-border/40">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="size-12 flex-shrink-0 bg-[#FBFAF7] text-[#131312] rounded-xl flex items-center justify-center border border-border/40 shadow-sm">
                      <FileText className="size-6" />
                    </div>
                    <h2 className="text-2xl font-semibold text-[#131312]">Quotation & Proposal</h2>
                  </div>
                  <p className="text-lg text-[#131312]/80 mb-6">
                    Within 24 hours, receive a detailed quotation including pricing, timeline, material specifications, and any special considerations for your project.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-center text-[#131312]">
                      <CheckCircle2 className="size-5 mr-3 flex-shrink-0" />
                      <span>Detailed pricing breakdown</span>
                    </li>
                    <li className="flex items-center text-[#131312]">
                      <CheckCircle2 className="size-5 mr-3 flex-shrink-0" />
                      <span>Production timeline estimate</span>
                    </li>
                    <li className="flex items-center text-[#131312]">
                      <CheckCircle2 className="size-5 mr-3 flex-shrink-0" />
                      <span>Material and finish options</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-8 items-center">
              <div className="flex-shrink-0">
                <div className="w-24 h-24 bg-[#131312] text-white rounded-2xl flex flex-col items-center justify-center shadow-xl">
                  <span className="text-2xl font-semibold">03</span>
                </div>
              </div>
              <div className="hidden md:block flex-shrink-0">
                <ArrowRight className="size-8 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <div className="bg-[#F6F2EB] rounded-2xl p-8 shadow-sm border border-border/40">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="size-12 flex-shrink-0 bg-[#FBFAF7] text-[#131312] rounded-xl flex items-center justify-center border border-border/40 shadow-sm">
                      <Package className="size-6" />
                    </div>
                    <h2 className="text-2xl font-semibold text-[#131312]">Sampling & Approval</h2>
                  </div>
                  <p className="text-lg text-[#131312]/80 mb-6">
                    Once you approve the quote, we create physical samples for your review. This crucial step ensures everything meets your expectations before full production.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-center text-[#131312]">
                      <CheckCircle2 className="size-5 mr-3 flex-shrink-0" />
                      <span>Physical samples with actual materials</span>
                    </li>
                    <li className="flex items-center text-[#131312]">
                      <CheckCircle2 className="size-5 mr-3 flex-shrink-0" />
                      <span>Color and finish verification</span>
                    </li>
                    <li className="flex items-center text-[#131312]">
                      <CheckCircle2 className="size-5 mr-3 flex-shrink-0" />
                      <span>Multiple revision rounds if needed</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-8 items-center">
              <div className="flex-shrink-0">
                <div className="w-24 h-24 bg-[#131312] text-white rounded-2xl flex flex-col items-center justify-center shadow-xl">
                  <span className="text-2xl font-semibold">04</span>
                </div>
              </div>
              <div className="hidden md:block flex-shrink-0">
                <ArrowRight className="size-8 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <div className="bg-[#F6F2EB] rounded-2xl p-8 shadow-sm border border-border/40">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="size-12 flex-shrink-0 bg-[#FBFAF7] text-[#131312] rounded-xl flex items-center justify-center border border-border/40 shadow-sm">
                      <Zap className="size-6" />
                    </div>
                    <h2 className="text-2xl font-semibold text-[#131312]">Production</h2>
                  </div>
                  <p className="text-lg text-[#131312]/80 mb-6">
                    With approved samples, production begins. Our state-of-the-art facilities and quality control processes ensure consistent results throughout the entire run.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-center text-[#131312]">
                      <CheckCircle2 className="size-5 mr-3 flex-shrink-0" />
                      <span>Professional printing and finishing</span>
                    </li>
                    <li className="flex items-center text-[#131312]">
                      <CheckCircle2 className="size-5 mr-3 flex-shrink-0" />
                      <span>Quality control at every stage</span>
                    </li>
                    <li className="flex items-center text-[#131312]">
                      <CheckCircle2 className="size-5 mr-3 flex-shrink-0" />
                      <span>Regular production updates</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-8 items-center">
              <div className="flex-shrink-0">
                <div className="w-24 h-24 bg-[#131312] text-white rounded-2xl flex flex-col items-center justify-center shadow-xl">
                  <span className="text-2xl font-semibold">05</span>
                </div>
              </div>
              <div className="hidden md:block flex-shrink-0">
                <CheckCircle2 className="size-8 text-[#131312]" />
              </div>
              <div className="flex-1">
                <div className="bg-[#F6F2EB] rounded-2xl p-8 shadow-sm border border-border/40">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="size-12 flex-shrink-0 bg-[#FBFAF7] text-[#131312] rounded-xl flex items-center justify-center border border-border/40 shadow-sm">
                      <Truck className="size-6" />
                    </div>
                    <h2 className="text-2xl font-semibold text-[#131312]">Delivery & Support</h2>
                  </div>
                  <p className="text-lg text-[#131312]/80 mb-6">
                    Your finished packaging is carefully packed and shipped to your location. We provide tracking information and are available for any post-delivery support you need.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-center text-[#131312]">
                      <CheckCircle2 className="size-5 mr-3 flex-shrink-0" />
                      <span>Global shipping with tracking</span>
                    </li>
                    <li className="flex items-center text-[#131312]">
                      <CheckCircle2 className="size-5 mr-3 flex-shrink-0" />
                      <span>Secure packaging for transit</span>
                    </li>
                    <li className="flex items-center text-[#131312]">
                      <CheckCircle2 className="size-5 mr-3 flex-shrink-0" />
                      <span>Ongoing customer support</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#F6F2EB]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-center text-[#131312]">Typical Timeline</h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            <div className="bg-white rounded-xl p-6 text-center border border-border/40">
              <div className="text-2xl font-semibold text-[#131312] mb-2">1-2 Days</div>
              <div className="text-sm font-medium text-[#131312] mb-2">Quotation</div>
              <p className="text-xs text-[#131312]/60">Quote delivered within 24 hours</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center border border-border/40">
              <div className="text-2xl font-semibold text-[#131312] mb-2">3-5 Days</div>
              <div className="text-sm font-medium text-[#131312] mb-2">Sampling</div>
              <p className="text-xs text-[#131312]/60">Physical samples created</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center border border-border/40">
              <div className="text-2xl font-semibold text-[#131312] mb-2">1-2 Days</div>
              <div className="text-sm font-medium text-[#131312] mb-2">Approval</div>
              <p className="text-xs text-[#131312]/60">Review and approve samples</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center border border-border/40">
              <div className="text-2xl font-semibold text-[#131312] mb-2">10-15 Days</div>
              <div className="text-sm font-medium text-[#131312] mb-2">Production</div>
              <p className="text-xs text-[#131312]/60">Manufacturing and QC</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center border border-border/40">
              <div className="text-2xl font-semibold text-[#131312] mb-2">3-7 Days</div>
              <div className="text-sm font-medium text-[#131312] mb-2">Delivery</div>
              <p className="text-xs text-[#131312]/60">Shipping to your location</p>
            </div>
          </div>
          <p className="text-center text-muted-foreground mt-8">
            Total time from inquiry to delivery: Approximately 3-4 weeks for standard orders
          </p>
        </div>
      </section>

      <section className="relative py-24 bg-[#131312] text-white overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              radial-gradient(72.35% 197.74% at 70% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%),
              radial-gradient(72.35% 197.74% at 30% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%)
            `
          }}
        />
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 text-white max-w-4xl mx-auto leading-[1.1]">
            Ready to Create Premium <br /> Packaging?
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            Get an instant quote and see how we can elevate your brand with custom packaging solutions
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/quote"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-white text-sm font-medium text-[#131312] transition-colors hover:bg-white/90 shadow-lg sm:h-14 sm:w-auto sm:px-8 sm:text-base"
            >
              Get Instant Quote
              <ArrowRight className="ml-2 size-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl border border-white/20 bg-white/10 px-8 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/20 sm:h-14 sm:w-auto sm:text-base"
            >
              Schedule Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
